import { Box, Flex, Image, Modal, ModalContent, ModalOverlay, useDisclosure } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { TextBold, TextMedium, TextRegular } from 'UiKit/typography';
import ButtonSolid from 'UiKit/button/buttonSolid';
import usePostEndBreak from 'Domain/monitoring/usePostEndBreak';
import { Condition } from 'Component/others';
import moment from 'moment';
import progressImage from 'Asset/image/progress-image.svg';
import CountTimer from 'Component/count-timer';
import { breakModalText } from 'Const/monitoring/view';
import { helpHostModalText } from 'Const/host-help-partner/view';
import { LocationIcon, MicrophoneIcon } from 'Asset/icon';
import useCaptchaSchedule from 'Store/captchaSchedule';

import HelpPartnerReasonModal from 'View/monitoring/component/help-partner-reason-modal';
import AudioViolation from 'View/monitoring/component/audio-violation';
import getBreakDescription from 'View/monitoring/helper/getBreakDescription';
import BreakModalSkeleton from 'View/monitoring/component/skeleton/break-modal-skeleton';

import styles from './styles.module.css';
import getBreakTitle from 'View/monitoring/helper/getBreakTitle';

const BreakModal = ({ monitoringStatus, isFetching }) => {
  const { t } = useTranslation('dashboard');
  const { isOpen: isTimeUp, onOpen: onTimeUp } = useDisclosure();
  const { isOpen: isOpenHelpPartnerReasonModal, onOpen: onOpenHelpPartnerReasonModal } = useDisclosure();
  const { mutate: onFinishBreak } = usePostEndBreak();
  const { triggerInit } = useCaptchaSchedule();

  const breakViolationDuration = monitoringStatus?.break?.breakViolationDuration;
  const breakCode = monitoringStatus?.break?.code;
  const isHelpPartner = breakCode === 'BANTU_REKAN';
  const helPartnerRequest = monitoringStatus?.break?.helpRequest;
  const isHelPartnerRequest = !isEmpty(helPartnerRequest);

  const onClickFinishBreak = () => {
    triggerInit();
    if (isHelpPartner) onOpenHelpPartnerReasonModal();
    else onFinishBreak({ monitoringStatus });
  };

  const buttonLabel = isHelpPartner ? t('breakModal.btnHelpPartnerText') : t('breakModal.btnDefaultText');
  const isViolation = isTimeUp && !isHelpPartner;
  const isShowBreakStartText = breakCode === 'SHOLAT';
  const startBreakText = moment(monitoringStatus?.break?.start).format('HH:mm:ss');
  const breakDescriptionText = getBreakDescription({ breakCode, t });
  const breakTitle = getBreakTitle({ breakCode, t });

  const isOpenBreakModal = !isOpenHelpPartnerReasonModal;

  return (
    <>
      <Modal isOpen={isOpenBreakModal}>
        <ModalOverlay />
        <ModalContent minWidth="600px">
          <Condition statement={!isFetching} fallback={<BreakModalSkeleton />}>
            <Box className={styles['break-modal__container']}>
              {/* Head */}
              <Box className={styles['break-modal__section--head']}>
                <Image src={progressImage} alt="progress-image" />
              </Box>

              {/* Content */}

              <Box className={styles['break-modal__section--content']}>
                <TextBold className={styles['break-title__text']}>{breakTitle}</TextBold>
                <Box className={styles['break-description__text']} dangerouslySetInnerHTML={{ __html: breakDescriptionText }} />

                {/* Help Partner Request */}

                <Condition statement={isHelPartnerRequest}>
                  <Flex className={styles['help-partner-request__container']}>
                    <Flex gap="4px">
                      <Flex className={styles['request-info__item']}>
                        <MicrophoneIcon color="var(--color-neutral60)" size="20px" />
                        <Box>
                          <TextRegular className={styles['request-info__text--label']}>{helpHostModalText.hostText}</TextRegular>
                          <TextBold className={styles['request-info__text--value']}>{helPartnerRequest?.hostName}</TextBold>
                        </Box>
                      </Flex>
                      <Flex className={styles['request-info__item']}>
                        <LocationIcon color="var(--color-neutral60)" size="20px" />
                        <Box>
                          <TextRegular className={styles['request-info__text--label']}>{helpHostModalText.roomText}</TextRegular>
                          <TextBold className={styles['request-info__text--value']}>{helPartnerRequest?.roomName}</TextBold>
                        </Box>
                      </Flex>
                    </Flex>
                    <Condition statement={helPartnerRequest?.notes}>
                      <Box className={styles['request-info__item']}>
                        <TextRegular className={styles['request-info__text--label']}>{helpHostModalText.notesText}</TextRegular>
                        <TextBold className={styles['request-info__text--value']}>{helPartnerRequest?.notes}</TextBold>
                      </Box>
                    </Condition>
                  </Flex>
                </Condition>

                {/* Break Start */}

                <Condition statement={isShowBreakStartText}>
                  <TextMedium className={styles['break-start__text']}>Mulai Istirahat pada {startBreakText}</TextMedium>

                  <Condition statement={isViolation}>
                    <CountTimer
                      className={styles['break-violation-duration__text']}
                      initialTime={breakViolationDuration}
                      isEnabled={isTimeUp}
                      variant="count-up"
                      prefix={'+'}
                    />
                  </Condition>
                </Condition>

                {/* Time Count */}

                <Box className={styles['break-timer__container']} bg={isHelPartnerRequest ? 'var(--color-semantic-background)' : 'none'}>
                  <Condition statement={!isShowBreakStartText}>
                    {/* Help Partner Count Up */}

                    <Condition statement={isHelpPartner}>
                      <Flex flexDirection={'column'} alignItems={'center'}>
                        <CountTimer
                          className={styles['break-timer__help-host--text']}
                          initialTime={monitoringStatus?.break?.helpPartnerDuration}
                          isEnabled
                          prefix={'+'}
                          variant="count-up"
                          maxMinutes={30}
                          onTimeUp={onTimeUp}
                        />
                        <Condition statement={isTimeUp}>
                          <TextBold className={styles['break-violation__help-host--text']}>{t('breakModal.warningText')}</TextBold>
                        </Condition>
                      </Flex>
                    </Condition>

                    {/* Break Solat & Toilet Count Down */}

                    <Condition statement={!isHelpPartner}>
                      <Flex alignItems="center" gap="16px" justifyContent="center">
                        <CountTimer
                          className={`${styles['break-timer__text']} ${isTimeUp ? styles['break-timer__text--disabled'] : ''} `}
                          initialTime={monitoringStatus?.break?.timeDiff}
                          isEnabled
                          prefix={'+'}
                          variant="count-down"
                          onTimeUp={onTimeUp}
                        />

                        {/* Break Violation */}

                        <Condition statement={isViolation}>
                          <CountTimer
                            className={styles['break-violation-duration__text']}
                            initialTime={breakViolationDuration}
                            isEnabled={isTimeUp}
                            prefix={'+'}
                            variant="count-up"
                          />
                        </Condition>
                      </Flex>
                    </Condition>
                  </Condition>
                </Box>

                <Condition statement={isViolation}>
                  <TextMedium className={styles['break-violation__text']}>{t('breakModal.warningRestText')}</TextMedium>
                </Condition>

                {/* Footer */}

                <ButtonSolid data-testid="finish-break-button" onClick={onClickFinishBreak} className={styles['finish-break__button']}>
                  {buttonLabel}
                </ButtonSolid>
              </Box>
            </Box>
          </Condition>
        </ModalContent>
      </Modal>

      {/*Help Partner Reason Modal */}

      <HelpPartnerReasonModal onSend={onFinishBreak} isOpen={isOpenHelpPartnerReasonModal} monitoringStatus={monitoringStatus} />

      {/* Audio Violation */}

      <Condition statement={isViolation}>
        <AudioViolation id="audio-violation__sound--break" />
      </Condition>
    </>
  );
};

export default BreakModal;
